import React, { createContext, useCallback, useState, useContext, useEffect } from 'react';
import { Goal } from '@/types/goals';
import { useAuthState } from '../hooks/useAuthState';
import { supabase, handleSupabaseError } from '../config/supabase';

interface GoalContextType {
  goals: Goal[];
  loading: boolean;
  addGoal: (goal: Omit<Goal, 'id'>) => Promise<void>;
  updateGoal: (goal: Goal) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  shareGoal: (goal: Goal) => void;
  openNewGoalModal: () => void;
  updateGoalProgress: (minutes: number) => void;
}

export const useGoals = () => {
  const context = useContext(GoalContext);
  if (!context) {
    throw new Error('useGoals must be used within a GoalProvider');
  }
  return context;
};

export const GoalContext = createContext<GoalContextType>({
  goals: [],
  loading: false,
  addGoal: async () => {},
  updateGoal: async () => {},
  deleteGoal: async () => {},
  shareGoal: () => {},
  openNewGoalModal: () => {},
  updateGoalProgress: () => {},
});

interface GoalProviderProps {
  children: React.ReactNode;
}

export const GoalProvider: React.FC<GoalProviderProps> = ({ children }) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNewGoalModalOpen, setIsNewGoalModalOpen] = useState(false);
  const { user } = useAuthState();

  // Load goals from Supabase
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const loadGoals = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('goals')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setGoals(data || []);
      } catch (error) {
        console.error('Error loading goals:', error);
        handleSupabaseError(error);
      } finally {
        setLoading(false);
      }
    };

    loadGoals();
  }, [user]);

  const addGoal = useCallback(async (goalData: Omit<Goal, 'id'>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase
        .from('goals')
        .insert({
          user_id: user.id,
          title: goalData.title,
          description: goalData.description,
          category: goalData.type,
          period: goalData.period,
          target_value: goalData.target,
          current_value: goalData.current,
          unit: goalData.type === 'focus' ? 'minutes' : 'tasks',
          start_date: goalData.startDate,
          end_date: goalData.endDate,
          is_public: false,
        })
        .select()
        .single();

      if (error) throw error;
      setGoals(prev => [data, ...prev]);
    } catch (error) {
      console.error('Error creating goal:', error);
      handleSupabaseError(error);
      throw error;
    }
  }, [user]);

  const updateGoal = useCallback(async (goal: Goal) => {
    try {
      const { data, error } = await supabase
        .from('goals')
        .update({
          title: goal.title,
          description: goal.description,
          category: goal.type,
          period: goal.period,
          target_value: goal.target,
          current_value: goal.current,
          start_date: goal.startDate,
          end_date: goal.endDate,
        })
        .eq('id', goal.id)
        .select()
        .single();

      if (error) throw error;
      setGoals(prev => prev.map(g => g.id === goal.id ? data : g));
    } catch (error) {
      console.error('Error updating goal:', error);
      handleSupabaseError(error);
      throw error;
    }
  }, []);

  const deleteGoal = useCallback(async (goalId: string) => {
    try {
      const { error } = await supabase
        .from('goals')
        .delete()
        .eq('id', goalId);

      if (error) throw error;
      setGoals(prev => prev.filter(g => g.id !== goalId));
    } catch (error) {
      console.error('Error deleting goal:', error);
      handleSupabaseError(error);
      throw error;
    }
  }, []);

  const shareGoal = useCallback((goal: Goal) => {
    // Implement share functionality
    console.log('Sharing goal:', goal);
  }, []);

  const openNewGoalModal = useCallback(() => {
    setIsNewGoalModalOpen(true);
  }, []);

  const updateGoalProgress = useCallback((minutes: number) => {
    setGoals(prevGoals => 
      prevGoals.map(goal => {
        if (goal.type !== 'focus') return goal;
        
        const newCurrent = goal.current + minutes;
        
        // Update milestones
        let updatedMilestones = goal.milestones?.map(milestone => ({
          ...milestone,
          completed: newCurrent >= milestone.target
        }));

        return {
          ...goal,
          current: newCurrent,
          milestones: updatedMilestones
        };
      })
    );
  }, []);

  return (
    <GoalContext.Provider 
      value={{
        goals,
        loading,
        addGoal,
        updateGoal,
        deleteGoal,
        shareGoal,
        openNewGoalModal,
        updateGoalProgress,
      }}
    >
      {children}
      {/* Add NewGoalModal component here when ready */}
    </GoalContext.Provider>
  );
};